import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Box } from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import { toast } from "react-toastify";

import { PageLayout } from "../../layouts/PageLayout";
import { AddEditAccount, TransactionList } from "../../components/account";
import { DeleteModal, PageHeader } from "../../components/v3";
import { AccountService } from "../../services";
import { DELETE_ACCOUNT, EDIT_ACCOUNT } from "../../constants";
import { useHasPermission } from "../../utils";
import { renameCashAccount } from "../../utils/accountUtils";

export const SingleAccount = () => {
  const [reload, setReload] = useState(0);
  const [accountData, setAccountData] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    accountId: null,
  });

  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);
  const { ownerUserId: companyAdminId } = user.company;

  const hasEditAccount = useHasPermission(EDIT_ACCOUNT);
  const hasDeleteAccount = useHasPermission(DELETE_ACCOUNT);

  const fetchData = useCallback(async () => {
    try {
      const response = await AccountService.getAccountById(id);
      setAccountData(response?.data);
    } catch (error) {
      console.error("🚀 ~ fetchData ~ error:", error);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData, id, reload]);

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const handleReload = () => setReload((prev) => prev + 1);

  const openDeleteModal = () => {
    setDeleteModal({
      isOpen: true,
      accountId: id,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      accountId: null,
    });
  };

  const handleDelete = async () => {
    try {
      await AccountService.deleteAccount(id);
      toast.success("Account Deleted Successfully");
      closeDeleteModal();
      navigate(-1);
    } catch (error) {
      console.error("🚀 ~ deleteEntity ~ error:", error);
      toast.error("Failed to delete Account");
    }
  };

  const renamedAccount = renameCashAccount(accountData, user, companyAdminId);
  const isCashAccount = ["CASH", "USER"].includes(accountData?.type);

  const buttons = !isCashAccount
    ? [
        hasEditAccount && {
          buttonText: "Edit",
          buttonIcon: Edit,
          onClick: handleOpenModal,
          style: {
            backgroundColor: "transparent",
            border: "1px solid white",
            color: "white",
          },
        },
        hasDeleteAccount && {
          buttonText: "Delete",
          buttonIcon: Delete,
          onClick: openDeleteModal,
          style: {
            backgroundColor: "transparent",
            border: "1px solid white",
            color: "white",
          },
        },
      ]
    : [];

  return (
    <>
      <PageLayout>
        {/* header section */}
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <PageHeader
            // title
            title={renamedAccount?.name}
            subtitle={`Balance: Rs: ${accountData.balance}`}
            // button
            buttons={buttons}
          />
        </Box>

        <Box sx={{ mt: -9, zIndex: 2, position: "relative" }}>
          <TransactionList name={renamedAccount?.name} onSuccess={handleReload} reloadData={reload} />
        </Box>
      </PageLayout>

      {/* edit account modal */}
      <AddEditAccount accountId={id} open={openModal} onClose={handleCloseModal} onSuccess={handleReload} />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this account?"
      />
    </>
  );
};
